import type { Mission } from '@/features/loader/types';
import type { AssetSpec } from '../types';
import { noaaEntry } from '@/features/loader/catalog/noaaCatalogue';
import { spacecraftReferenceFor } from '@/features/loader/globe/spacecraftModels';

export function missionTemplateFor(mission: Pick<Mission, 'id' | 'name' | 'noradId'>): string | null {
  if (noaaEntry(mission.id)) return noaaEntry(mission.id, mission.noradId)?.template ?? null;
  const reference = spacecraftReferenceFor(mission);
  if (reference) return reference.template;
  const name = `${mission.id} ${mission.name}`;
  if (/\b(suomi[-_ ]?npp|s[-_ ]npp)\b/i.test(name)) return 'suomi_npp';
  if (/\b(noaa[-_ ]?21|jpss[-_ ]?2)\b/i.test(name)) return 'noaa21';
  if (/\b(ace|advanced composition explorer)\b/i.test(name)) return 'ace';
  if (/\bdscovr\b/i.test(name)) return 'dscovr';
  if (/jason.?3/i.test(name)) return 'senseSat_01';
  if (/\bgoes[-_ ]?(16|17|18)\b/i.test(name)) return 'goes';
  if (/tdrs/i.test(name)) return 'tdrs';
  return null;
}

export function assetSpecForMission(
  mission: Pick<Mission, 'id' | 'name' | 'noradId'>,
  network: string,
  template?: AssetSpec,
): AssetSpec {
  const entry = network === 'noaa' ? noaaEntry(mission.id, mission.noradId) : undefined;
  return {
    ...(template ?? {
      elementId: 'generic_satellite',
      procedural: {
        kind: 'satellite' as const,
        bus: 'box' as const,
        lengthM: 2,
        solarWings: 2 as const,
        dishes: 1,
      },
      poses: {
        main: {
          position: [7, 4, 9] as [number, number, number],
          target: [0, 0, 0] as [number, number, number],
          fov: 45,
        },
      },
      bindings: [],
    }),
    ...(entry
      ? {
          elementId: `noaa_reference:${entry.id}`,
          space: true,
          ...(!template
            ? {
                modelUrl: '/models/generic-satellite.glb',
                // One generic illustration in both renderers; dimensions are not spacecraft measurements.
                bindings: [
                  { subsystemId: 'reference_bus', nodes: ['bus'], label: 'Illustrative bus' },
                  {
                    subsystemId: 'reference_wings',
                    nodes: ['solar_wings'],
                    label: 'Illustrative solar wings',
                  },
                  { subsystemId: 'reference_dish', nodes: ['dish_1'], label: 'Illustrative antenna' },
                ],
              }
            : {}),
        }
      : {}),
    id: `mission:${encodeURIComponent(network)}:${encodeURIComponent(mission.id)}`,
    label: mission.name,
    modelNote:
      entry && !template
        ? 'Generic spacecraft illustration. Shape, size and instrument locations are not a replica of this mission. Published payload references are separate; no live subsystem telemetry.'
        : (template?.modelNote ??
          (template
            ? 'Reference model; telemetry is simulated unless explicitly connected.'
            : 'Representative spacecraft; detailed model and subsystem data pending.')),
    globeLink: { network, missionId: mission.id },
  };
}
